import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { journalEntriesApi } from "../api/journalEntries";
import { rciApi } from "../api/rci";
import { BottomNav } from "../components/BottomNav";
import SharedEntriesModal from "../components/SharedEntriesModal";
import { useActivePractitionerLink } from "../hooks/useActivePractitionerLink";
import { PALETTE } from "../tokens";
import {
  fullDayLabel,
  localDateKey,
  mondayOf,
  weekHeader,
} from "../utils/weekGrouping";

// /journal — the free-write timeline.
//
// Structurally SignalHistory.jsx: reverse-chronological cards grouped
// under Monday-anchored "Week of …" dividers, an IntersectionObserver
// sentinel paging with a strict `before=` date cursor, today's card
// dark and the rest white. Two differences, both from the data:
//
//   · The source is GET /journal-entries (entries), not RCI snapshots.
//     A card shows a truncated body instead of a score, because a
//     free-write entry HAS no score — it's excluded from RCI by the
//     shape of the extraction binding, not by a display choice.
//   · A date can hold MANY entries (journal_entries has no per-day
//     unique constraint), so cards key on entry id, not on date, and
//     the server completes a trailing date rather than cutting a page
//     mid-day.
//
// Tapping a card opens a read-only detail overlay rendered from the
// already-loaded entry. There is no update endpoint in the API — see
// the comment on EntryDetail. The detail view is also where this tab's
// two outbound links live: sharing an entry with a practitioner (only
// when there IS one) and opening that day's signal. Both are absent
// rather than inert when their data doesn't exist.
//
// `?date=YYYY-MM-DD` narrows to one day. That's the landing target for
// the Signal-History cross-link, which asks "does this day have a
// free-write entry?" and routes here when it does.

const PAGE_LIMIT = 30;
const ANTIQUE_GOLD = "#9E8456";
const WARM_BLACK = "#1C1814";
const TEXT_SECONDARY = "rgba(28,24,20,0.62)";
const BORDER = "rgba(28,24,20,0.10)";
const SERIF = "'Playfair Display', serif";

const PREVIEW_CHARS = 110;

function preview(body) {
  const text = (body || "").trim().replace(/\s+/g, " ");
  if (text.length <= PREVIEW_CHARS) return text;
  return `${text.slice(0, PREVIEW_CHARS).trimEnd()}…`;
}

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

// The day after `iso`, computed in local time. GET /history takes a
// STRICT `before=`, so asking for the day after the entry's date makes
// that date itself the first row that can come back — the same anchoring
// trick DayDetail.jsx uses for its snapshot window.
function nextDayKey(iso) {
  const [y, m, d] = (iso || "").split("-").map(Number);
  if (!y || !m || !d) return null;
  const next = new Date(y, m - 1, d + 1);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}-${String(next.getDate()).padStart(2, "0")}`;
}


function EntryCard({ entry, isToday, onTap }) {
  // Same dark-today / white-older treatment as SignalHistory's DayCard,
  // same 12px radius and internal spacing, so the two timelines read as
  // one pattern applied to two kinds of day.
  const isDark = isToday;
  const bg = isDark ? WARM_BLACK : "white";
  const titleColor = isDark ? PALETTE.surface : WARM_BLACK;
  // On the dark today-card the body text is the surface token softened by
  // opacity rather than a hand-mixed rgba — the alpha lives in CSS so the
  // colour itself stays a palette value. Light mode's TEXT_SECONDARY already
  // carries its own alpha, so it renders at full opacity.
  const bodyColor = isDark ? PALETTE.surface : TEXT_SECONDARY;
  const bodyOpacity = isDark ? 0.72 : 1;
  const border = isDark ? "none" : `0.5px solid ${BORDER}`;
  const dateLine = fullDayLabel(entry.entry_date);
  const time = timeLabel(entry.created_at);

  return (
    <button
      type="button"
      onClick={() => onTap(entry)}
      style={{
        all: "unset",
        cursor: "pointer",
        display: "block",
        background: bg,
        borderRadius: 12,
        padding: "12px 14px",
        border,
        boxSizing: "border-box",
        width: "100%",
      }}
      aria-label={`Open entry from ${dateLine}`}
    >
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        marginBottom: 6, gap: 10,
      }}>
        <span style={{ fontSize: 13, color: titleColor, fontWeight: 500 }}>
          {dateLine}
        </span>
        {/* Where SignalHistory shows a score. A free-write entry has no
            number to show, and inventing one would be a lie about what
            this surface is — the time it was written is the honest
            equivalent. */}
        <span style={{
          fontSize: 12, color: ANTIQUE_GOLD, fontFamily: SERIF,
          lineHeight: 1, flexShrink: 0,
        }}>
          {time}
        </span>
      </div>
      <p style={{
        fontSize: 12, color: bodyColor, opacity: bodyOpacity,
        margin: 0, lineHeight: 1.5,
      }}>
        {preview(entry.body)}
      </p>
    </button>
  );
}


// Read-only. Phase 2 shipped create + list only: there is no PATCH
// /journal-entries/{id}, so an editable box here would accept keystrokes
// it could never persist. A fake edit on a journal is worse than no
// edit — the user would believe a correction landed. If editing is
// wanted for v1 it needs an update endpoint first.
//
// Two affordances live here that the list deliberately doesn't carry:
//
//   · Share with your practitioner — only when there IS one (see
//     useActivePractitionerLink; same gate ProtocolCard uses on /dashboard,
//     one shared rule rather than two copies of it). Sharing opens the
//     usual receipt-first modal, focused on this entry.
//   · That day's signal — a link to /history/:date, when a snapshot
//     exists for the entry's date. It is a NUMBER FOR THE DAY, not for
//     the entry: nothing written here fed it, and the line under it says
//     so, because a score sitting next to a piece of writing invites
//     exactly the inference that is false about this surface.
function EntryDetail({ entry, onClose, practitionerLink }) {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [daySignal, setDaySignal] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  // "Is there a snapshot for the day this was written?" — one row,
  // anchored on the entry's own date. Soft-fails to null: no snapshot
  // and a failed lookup both render no affordance, which is the same
  // don't-manufacture-noise rule the day view uses in the other
  // direction.
  useEffect(() => {
    const before = nextDayKey(entry?.entry_date);
    if (!user?.id || !token || !before) return;
    let cancelled = false;
    // No reset here — the parent keys this component on the entry id,
    // so a different entry gets a fresh mount (and a fresh null) rather
    // than a stale number in the gap before the fetch lands.
    rciApi.history(user.id, 1, token, before).then((rows) => {
      if (cancelled) return;
      const row = (Array.isArray(rows) ? rows : [])
        .find((s) => s.window_end === entry.entry_date);
      if (row?.rci_total != null) {
        setDaySignal(Math.max(1, Math.round(row.rci_total * 100)));
      }
    }).catch(() => { /* no cross-link rather than a broken one */ });
    return () => { cancelled = true; };
  }, [user?.id, token, entry?.entry_date]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Entry from ${fullDayLabel(entry.entry_date)}`}
      style={{
        position: "fixed", inset: 0, zIndex: 1000, background: PALETTE.surface,
        overflowY: "auto",
        paddingTop: "max(20px, calc(env(safe-area-inset-top) + 12px))",
        paddingBottom: "max(28px, calc(env(safe-area-inset-bottom) + 12px))",
      }}
    >
      <div style={{ maxWidth: 420, margin: "0 auto", padding: "0 16px" }}>
        <div style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 24,
        }}>
          <button
            type="button"
            onClick={onClose}
            aria-label="Back"
            style={{
              all: "unset", cursor: "pointer",
              fontSize: 20, color: ANTIQUE_GOLD, minWidth: 28,
            }}
          >
            ←
          </button>
          <span style={{
            fontSize: 11, fontWeight: 500, color: ANTIQUE_GOLD,
            letterSpacing: "0.08em", textTransform: "uppercase",
          }}>
            Entry
          </span>
        </div>

        <h1 style={{
          fontFamily: SERIF, fontSize: 24, fontStyle: "italic",
          fontWeight: 400, color: WARM_BLACK, lineHeight: 1.3,
          margin: "0 0 4px",
        }}>
          {fullDayLabel(entry.entry_date)}
        </h1>
        {timeLabel(entry.created_at) && (
          <p style={{ fontSize: 12, color: TEXT_SECONDARY, margin: "0 0 20px" }}>
            Written at {timeLabel(entry.created_at)}
          </p>
        )}

        <p style={{
          fontSize: 14, color: WARM_BLACK, lineHeight: 1.8,
          whiteSpace: "pre-wrap", margin: 0,
        }}>
          {entry.body}
        </p>

        {/* That day's signal. Absent entirely when no snapshot exists
            for the date — an empty score slot would be a worse answer
            than no slot. */}
        {daySignal != null && (
          <>
            <button
              type="button"
              onClick={() => navigate(`/history/${entry.entry_date}`)}
              aria-label={`Open the signal for ${fullDayLabel(entry.entry_date)}`}
              style={{
                all: "unset",
                cursor: "pointer",
                boxSizing: "border-box",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
                background: "white",
                border: `0.5px solid ${BORDER}`,
                borderRadius: 12,
                padding: "12px 14px",
                margin: "28px 0 0",
              }}
            >
              <span>
                <span style={{
                  display: "block", fontSize: 10, color: ANTIQUE_GOLD,
                  fontWeight: 500, letterSpacing: "0.08em", marginBottom: 4,
                }}>
                  THAT DAY&rsquo;S SIGNAL
                </span>
                <span style={{ fontSize: 12, color: TEXT_SECONDARY }}>
                  {fullDayLabel(entry.entry_date)}
                </span>
              </span>
              <span style={{
                display: "flex", alignItems: "center", gap: 8, flexShrink: 0,
              }}>
                <span style={{ fontSize: 20, color: ANTIQUE_GOLD, fontFamily: SERIF, lineHeight: 1 }}>
                  {daySignal}
                </span>
                <span style={{ fontSize: 15, color: ANTIQUE_GOLD }}>&rsaquo;</span>
              </span>
            </button>
            <p style={{
              fontSize: 11, color: TEXT_SECONDARY, lineHeight: 1.6,
              margin: "8px 0 0",
            }}>
              What you write here isn&rsquo;t part of that number.
            </p>
          </>
        )}

        {/* Share. Invisible without a live practitioner relationship —
            the same gate ProtocolCard applies on /dashboard, so this
            never opens onto a relationship that doesn't exist. */}
        {practitionerLink && (
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            style={{
              all: "unset",
              cursor: "pointer",
              boxSizing: "border-box",
              display: "block",
              textAlign: "center",
              width: "100%",
              border: `0.5px solid ${BORDER}`,
              borderRadius: 12,
              padding: "13px 14px",
              margin: "12px 0 0",
              fontSize: 13,
              color: ANTIQUE_GOLD,
            }}
          >
            Share with {practitionerLink.practitioner_name || "your practitioner"}
          </button>
        )}

        {shareOpen && practitionerLink && (
          <SharedEntriesModal
            link={practitionerLink}
            focusEntryId={entry.id}
            onClose={() => setShareOpen(false)}
          />
        )}
      </div>
    </div>
  );
}


export default function Journal() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const dateFilter = searchParams.get("date");

  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [openEntry, setOpenEntry] = useState(null);
  const sentinelRef = useRef(null);
  // Null unless there's an active, consented practitioner protocol.
  // Drives whether the detail view offers sharing at all.
  const practitionerLink = useActivePractitionerLink();

  const today = useMemo(() => localDateKey(), []);

  // Initial load. A `?date=` filter is a single-day lookup: one page by
  // construction, so paging is switched off for it.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const request = dateFilter
      ? journalEntriesApi.listByDate(dateFilter, token)
      : journalEntriesApi.list(token, { limit: PAGE_LIMIT });

    request.then((res) => {
      if (cancelled) return;
      setEntries(Array.isArray(res?.entries) ? res.entries : []);
      setHasMore(!dateFilter && Boolean(res?.next_before));
    }).catch(() => {
      if (!cancelled) setError("Couldn't load your entries.");
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [token, dateFilter]);

  // Paginate when the sentinel enters the viewport. The cursor is the
  // oldest loaded entry_date, passed back as a strict `before=`, which
  // is why the server completes a trailing date before handing one out.
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore || dateFilter || !entries.length) return;
    const oldest = entries[entries.length - 1];
    if (!oldest?.entry_date) return;
    setLoadingMore(true);
    journalEntriesApi.list(token, {
      limit: PAGE_LIMIT, before: oldest.entry_date,
    }).then((res) => {
      const page = Array.isArray(res?.entries) ? res.entries : [];
      setEntries((prev) => {
        // The server guarantees no overlap across a strict-date cursor,
        // but de-duping by id makes a double-fired observer harmless
        // rather than a source of duplicate React keys.
        const seen = new Set(prev.map((e) => e.id));
        return [...prev, ...page.filter((e) => !seen.has(e.id))];
      });
      setHasMore(Boolean(res?.next_before));
    }).catch(() => {
      // Soft-fail: the cards already on screen stay.
      setHasMore(false);
    }).finally(() => setLoadingMore(false));
  }, [token, entries, hasMore, loadingMore, dateFilter]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (obsEntries) => obsEntries.forEach((e) => { if (e.isIntersecting) loadMore(); }),
      { rootMargin: "200px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  // Group by Monday-of-week. `entries` is newest-first, so Map
  // iteration order is newest-first too.
  const grouped = useMemo(() => {
    const map = new Map();
    entries.forEach((e) => {
      const key = mondayOf(e.entry_date);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(e);
    });
    return map;
  }, [entries]);

  const clearFilter = () => setSearchParams({}, { replace: true });

  return (
    <div style={{
      minHeight: "100vh",
      background: PALETTE.surface,
      padding: "max(20px, calc(env(safe-area-inset-top) + 12px)) 16px var(--nav-clearance)",
      boxSizing: "border-box",
    }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 10, marginBottom: 24,
      }}>
        <span style={{
          fontSize: 11, fontWeight: 500, color: ANTIQUE_GOLD,
          letterSpacing: "0.08em", textTransform: "uppercase",
        }}>
          Journal
        </span>
        <button
          type="button"
          onClick={() => navigate("/journal/new")}
          style={{
            all: "unset", cursor: "pointer",
            fontSize: 12, color: ANTIQUE_GOLD,
            border: `0.5px solid ${BORDER}`, borderRadius: 99,
            padding: "6px 14px",
          }}
        >
          New entry
        </button>
      </div>

      {dateFilter && (
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 10, marginBottom: 16, background: "white",
          border: `0.5px solid ${BORDER}`, borderRadius: 12,
          padding: "10px 14px",
        }}>
          <span style={{ fontSize: 12, color: WARM_BLACK }}>
            {fullDayLabel(dateFilter)}
          </span>
          <button
            type="button"
            onClick={clearFilter}
            style={{
              all: "unset", cursor: "pointer",
              fontSize: 12, color: ANTIQUE_GOLD, flexShrink: 0,
            }}
          >
            Show all
          </button>
        </div>
      )}

      {error && <p style={{ fontSize: 13, color: WARM_BLACK }}>{error}</p>}

      {loading && <p style={{ fontSize: 13, color: TEXT_SECONDARY }}>Loading…</p>}

      {!loading && !error && entries.length === 0 && (
        <p style={{ fontSize: 13, color: TEXT_SECONDARY, lineHeight: 1.6 }}>
          {dateFilter
            ? "Nothing written on this day."
            : "Nothing here yet. This is the page with nothing asked of it — what you write stays out of your signal."}
        </p>
      )}

      {/* Weekly groups */}
      {!loading && Array.from(grouped.entries()).map(([weekKey, weekEntries]) => (
        <section key={weekKey} style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 12 }}>
            <span style={{ fontSize: 12, color: ANTIQUE_GOLD }}>
              {weekHeader(weekKey)}
            </span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {weekEntries.map((entry) => (
              <EntryCard
                key={entry.id}
                entry={entry}
                isToday={entry.entry_date === today}
                onTap={setOpenEntry}
              />
            ))}
          </div>
        </section>
      ))}

      {/* Sentinel for infinite scroll + pagination state */}
      <div ref={sentinelRef} style={{ height: 1 }} />
      {loadingMore && (
        <p style={{
          textAlign: "center", fontSize: 12, color: TEXT_SECONDARY, marginTop: 12,
        }}>
          Loading older weeks…
        </p>
      )}
      {!loading && !loadingMore && !hasMore && !dateFilter && entries.length > 0 && (
        <p style={{
          textAlign: "center", fontSize: 12, color: TEXT_SECONDARY, marginTop: 12,
        }}>
          You&rsquo;ve reached your first entry.
        </p>
      )}

      {/* Mounted only while an entry is open — the detail view runs its
          own fetches now, and they should start when it opens rather
          than idling behind a null guard. */}
      {openEntry && (
        <EntryDetail
          key={openEntry.id}
          entry={openEntry}
          onClose={() => setOpenEntry(null)}
          practitionerLink={practitionerLink}
        />
      )}

      <BottomNav />
    </div>
  );
}
